// authService.js - Authentication related services
import { 
  useAuthStore, 
  resetApp
} from '../state';
import fetchWithToken from './api';
import { refreshAccessToken } from './tokenService';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/**
 * Verifies the authentication status of the user by checking token validity.
 * If the current access token is valid, sets the user as logged in.
 * If the token has expired, attempts to refresh it.
 * If refresh fails, sets the user as logged out.
 * @async
 * @returns {Promise<void>}
 */
export const checkAuthStatus = async () => {
    const accessToken = useAuthStore.getState().accessToken;
    const tokenExpiry = useAuthStore.getState().tokenExpiry;

    try {
        const currentTime = Date.now();
        const tokenIsValid = accessToken && tokenExpiry && currentTime < tokenExpiry;

        if (tokenIsValid) {
            useAuthStore.getState().setIsLoggedIn(true);
            return;
        }

        await refreshAccessToken();
        useAuthStore.getState().setIsLoggedIn(true);
    } catch (error) {
        console.error('Error checking if user is logged in:', error);
        useAuthStore.getState().setIsLoggedIn(false);
        return;
    }
};

/**
 * Authenticates a user with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} User data
 */
export const login = async (email, password) => {
    try {
        const response = await fetch(`${apiBaseUrl}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
            credentials: 'include', // Include cookies for refresh token
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to login');
        }

        const data = await response.json();
        console.log('data returned from /login endpoint: ', data);
            
        // Update app state with token information
        const expiryTime = Date.now() + (data.expiresIn * 1000);
        useAuthStore.getState().setAccessToken(data.accessToken);
        useAuthStore.getState().setTokenExpiry(expiryTime);
        useAuthStore.getState().setIsLoggedIn(true);

        return data;
    } catch (error) {
        console.error('Error during login:', error);
        throw error;
    }
};

/**
 * Authenticates a user via Google credential (One Tap / Identity Services)
 * @param {string} credential - Google ID token (JWT) from Google callback
 * @returns {Promise<Object>} Response data containing accessToken and expiresIn
 */
export const googleLogin = async (credential) => {
    try {
        const response = await fetch(`${apiBaseUrl}/google-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ credential }),
            credentials: 'include', // Ensure refresh cookie is accepted
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Google authentication failed');
        }

        const data = await response.json();

        // Update app state with token information
        const expiryTime = Date.now() + (data.expiresIn * 1000);
        useAuthStore.getState().setAccessToken(data.accessToken);
        useAuthStore.getState().setTokenExpiry(expiryTime);
        useAuthStore.getState().setIsLoggedIn(true);

        return data;
    } catch (error) {
        console.error('Error during Google login:', error);
        throw error;
    }
};

/**
 * Authenticates a user via Google OAuth 2.0 Code flow
 * Requests only 'openid email' on the consent screen
 * @param {string} code - Authorization code returned by GIS code client
 * @returns {Promise<Object>} Response data containing accessToken and expiresIn
 */
export const googleLoginWithCode = async (code) => {
    try {
        const response = await fetch(`${apiBaseUrl}/google-oauth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XmlHttpRequest',
            },
            body: JSON.stringify({ code }),
            credentials: 'include', // Ensure refresh cookie is accepted
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Google authentication failed');
        }

        const data = await response.json();

        // Update app state with token information
        const expiryTime = Date.now() + (data.expiresIn * 1000);
        useAuthStore.getState().setAccessToken(data.accessToken);
        useAuthStore.getState().setTokenExpiry(expiryTime);
        useAuthStore.getState().setIsLoggedIn(true);

        return data;
    } catch (error) {
        console.error('Error during Google OAuth code login:', error);
        throw error;
    }
};

/**
 * Logs out the current user
 */
export const logout = async () => {
    try {
        // We need to import fetchWithToken directly to avoid circular dependency
        await fetchWithToken('/logout', {
            method: 'POST',
            credentials: 'include' // Required to send the refresh token cookie
        });
    } catch (error) {
        console.error('Error during logout:', error);
    } finally {
        resetApp();
        
        // Explicitly set isLoggedIn to false
        useAuthStore.getState().setIsLoggedIn(false);
    }
};
